// frontend/src/hooks/useInbox.ts
// Unified inbox (DMs + groups + channels) for the messenger dock + /comms page.
// Loads /api/comms/conversations/inbox, hydrates the messengerStore unread totals, and stays
// live off the shared WebSocket: a `unread:update` on the caller's person
// channel reorders + bumps the list WITHOUT a refetch (§7 24-channel-cap fix);
// other comms events trigger a debounced refetch.
import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../utils/api'
import { useSocket } from '../lib/socket-context.jsx'
import { useMessengerStore } from '../store/messengerStore.js'

export interface ConvSummary {
  id: number
  kind: 'dm' | 'group' | 'channel'
  /** How the channel was created: 'auto_production' = a production's auto-group. */
  created_kind?: string | null
  title: string | null
  production_id: number | null
  last_message_preview: string | null
  last_message_at: string | null
  unread_count: number
  muted: boolean
}

export interface UseInboxOptions {
  /** Show only archived conversations instead of active ones (default false). */
  archived?: boolean
}

const REFETCH_DEBOUNCE_MS = 600

export function useInbox(options: UseInboxOptions = {}) {
  const { archived = false } = options
  const [items, setItems] = useState<ConvSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { addMessageListener } = (useSocket() || {}) as {
    addMessageListener?: (cb: (msg: { type?: string; conversationId?: number; preview?: string; lastMessageAt?: string; unreadDelta?: number } | null) => void) => (() => void) | void
  }
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    try {
      const url = archived
        ? '/comms/conversations/inbox?archived=1'
        : '/comms/conversations/inbox'
      const { data } = await api.get<{ items: ConvSummary[] }>(url)
      const list: ConvSummary[] = data?.items ?? []
      setItems(list)
      // Seed the store's per-conversation unread → totalUnread badge from the
      // active inbox only — archived view is a read-only filter, not the badge
      // source of truth.
      if (!archived) {
        const store = useMessengerStore.getState()
        for (const c of list) store.applyUnread(c.id, c.unread_count || 0)
      }
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox')
    } finally {
      setLoading(false)
    }
  }, [archived])

  useEffect(() => { load() }, [load])

  // Live updates from the caller's person channel.
  useEffect(() => {
    if (!addMessageListener) return
    const off = addMessageListener((msg) => {
      if (!msg) return
      if (msg.type === 'unread:update' && msg.conversationId != null) {
        // Reorder + bump in place — no refetch.
        setItems((prev) => {
          const idx = prev.findIndex((c) => c.id === msg.conversationId)
          if (idx === -1) {
            // Unknown conversation (new DM) — refetch to pick it up.
            scheduleRefetch()
            return prev
          }
          const updated = {
            ...prev[idx],
            last_message_preview: msg.preview ?? prev[idx].last_message_preview,
            last_message_at: msg.lastMessageAt ?? prev[idx].last_message_at,
            unread_count: prev[idx].unread_count + (msg.unreadDelta ?? 0),
          }
          const store = useMessengerStore.getState()
          store.applyUnread(updated.id, updated.unread_count)
          // Move to the top.
          const next = [...prev.slice(0, idx), ...prev.slice(idx + 1)]
          next.unshift(updated)
          return next
        })
      } else if (msg.type === 'dm.conversation.deleted' && msg.conversationId != null) {
        // A conversation was deleted for everyone — drop it from the inbox in
        // place (no refetch needed; it's gone server-side).
        setItems((prev) => {
          const idx = prev.findIndex((c) => c.id === msg.conversationId)
          if (idx === -1) return prev
          useMessengerStore.getState().applyUnread(msg.conversationId!, 0)
          return [...prev.slice(0, idx), ...prev.slice(idx + 1)]
        })
      } else if (
        msg.type === 'dm.message.new' ||
        msg.type === 'dm.conversation.new' ||
        msg.type === 'dm.message.deleted'
      ) {
        scheduleRefetch()
      }
    })
    function scheduleRefetch() {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => { load() }, REFETCH_DEBOUNCE_MS)
    }
    return () => {
      if (typeof off === 'function') off()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [addMessageListener, load])

  return { items, loading, error, reload: load }
}

/** Total unread badge — one cheap query (mirrors GET /api/comms/conversations/inbox/unread-total). */
export async function fetchUnreadTotal(): Promise<number> {
  try {
    const { data } = await api.get<{ total: number }>('/comms/conversations/inbox/unread-total')
    return data?.total ?? 0
  } catch {
    return 0
  }
}
